import { Snapshot } from "@custom-types/base-types";
import {
  RequiredPermissionSnapshotLevelName,
  RequiredPermissionsSnapshotLevel,
} from "@utils/permission-control/snapshot-permission-control-types";

/**
 * Object that determines all different permission rules for users to get
 * access on the snapshot level.
 */
export const requiredPermissionsSnapshotLevel: RequiredPermissionsSnapshotLevel =
  {
    /** Whether the user can manage the snapshot (rename, delete, change access level) */
    [RequiredPermissionSnapshotLevelName.canManageSnapshot]: {
      permissions: ["*", "admin"],
    },

    /** Whether the user can view all members with access to the snapshot */
    [RequiredPermissionSnapshotLevelName.canViewAllSnapshotMembers]: {
      permissions: ["*", "admin", "write"],
    },
  };

/**
 * Checks whether a user has permissions within the snapshot level.
 *
 * @returns True if the user has a valid snapshot level permission.
 */
export function hasUserValidPermissionSnapshotLevel(
  snapshot: Snapshot,
  permissionName: RequiredPermissionSnapshotLevelName
): boolean {
  // Return true if user has all permission, further checking not required
  if (snapshot.permissions.includes("*")) {
    return true;
  }

  const requiredPermissions =
    requiredPermissionsSnapshotLevel[permissionName].permissions;

  // If there is no required permissions that means user doesn't required any permission
  if (requiredPermissions.length === 0) {
    return true;
  }

  // Check for the necessary permission to allow users when they have one of the required permissions on snapshot level
  return requiredPermissions.some((requiredPermission) =>
    snapshot.permissions.includes(requiredPermission)
  );
}

/** Checks if the user has admin permissions for the snapshot */
export function isUserSnapshotAdmin(snapshot: Snapshot): boolean {
  return hasUserValidPermissionSnapshotLevel(
    snapshot,
    RequiredPermissionSnapshotLevelName.canManageSnapshot
  );
}

/** Checks if the user can view the members of the snapshot */
export function canUserViewSnapshotMembers(snapshot: Snapshot): boolean {
  return hasUserValidPermissionSnapshotLevel(
    snapshot,
    RequiredPermissionSnapshotLevelName.canViewAllSnapshotMembers
  );
}

/**
 * Checks if the user has view-only permissions for the snapshot.
 * View-only means the user has only describe/read permissions with no write, admin, or wildcard access.
 */
export function isUserSnapshotViewOnly(snapshot: Snapshot): boolean {
  // Return false if user has permissions that allow more than just view access
  return !(
    snapshot.permissions.includes("*") ||
    snapshot.permissions.includes("admin") ||
    snapshot.permissions.includes("write")
  );
}
